import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Globe } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Logo } from "@/components/site/Logo";
import { COMPANY } from "@/lib/company";

const NAV_LABELS = {
  en: { home: "Home", team: "Team", contact: "Contact", access: "Access", portal: "My space", login: "Sign in", language: "Language" },
  fr: { home: "Accueil", team: "Équipe", contact: "Contact", access: "Accès", portal: "Mon espace", login: "Se connecter", language: "Langue" },
  es: { home: "Inicio", team: "Equipo", contact: "Contacto", access: "Acceso", portal: "Mi espacio", login: "Entrar", language: "Idioma" },
};

export const Header = () => {
  const { lang, setLang } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const L = NAV_LABELS[lang] || NAV_LABELS.en;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const primary = [
    { to: "/",             label: L.home,    tid: "nav-home" },
    { to: "/team",         label: L.team,    tid: "nav-team" },
    { to: "/contact",      label: L.contact, tid: "nav-contact" },
  ];

  const langs = [{ c: "en", n: "English" }, { c: "fr", n: "Français" }, { c: "es", n: "Español" }];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background,border,backdrop-filter] duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-black/5" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-4 md:px-10 h-16 md:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-link" className="flex items-center shrink-0">
          <Logo theme="dark" size={28} />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {primary.map(n => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={n.tid}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors hover:text-black ${isActive ? "text-black" : "text-zinc-600"}`
              }>{n.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3 shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-switcher"
                className="flex items-center gap-1.5 text-[12px] tracking-wide text-zinc-500 hover:text-black transition-colors px-2 py-1"
              >
                <Globe className="w-3.5 h-3.5" />
                <span className="uppercase font-medium">{lang}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              <DropdownMenuLabel className="text-[11px] tracking-widest text-zinc-500 uppercase">{L.language}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {langs.map(l => (
                <DropdownMenuItem
                  key={l.c}
                  data-testid={`lang-option-${l.c}`}
                  onClick={() => setLang(l.c)}
                  className={lang === l.c ? "font-medium" : ""}
                >
                  {l.n}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {user ? (
            <Button
              data-testid="header-portal-btn"
              onClick={() => navigate(user.role === "admin" ? "/admin" : "/portal")}
              size="sm"
              className="hidden lg:inline-flex rounded-full h-9 px-5 text-[12px] tracking-wide bg-black hover:bg-zinc-800 text-white"
            >
              {L.portal}
            </Button>
          ) : (
            <Button
              data-testid="header-access-btn"
              onClick={() => navigate("/login")}
              size="sm"
              variant="outline"
              className="hidden lg:inline-flex rounded-full h-9 px-5 text-[12px] tracking-wide border-black/20 text-black hover:bg-black hover:text-white"
            >
              {L.access}
            </Button>
          )}

          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden p-2"
            onClick={() => setOpen(v => !v)}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden bg-white border-t border-black/5 max-h-[calc(100vh-4rem)] overflow-y-auto"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {primary.map(l => (
                <NavLink key={l.to} to={l.to} end={l.to === "/"}
                  data-testid={`mobile-${l.tid}`}
                  className={({ isActive }) => `py-3 text-base ${isActive ? "text-black font-medium" : "text-zinc-700"}`}
                >{l.label}</NavLink>
              ))}
              <div className="flex flex-col gap-3 pt-4 mt-3 border-t border-black/10">
                <Button
                  data-testid="mobile-access-btn"
                  onClick={() => navigate(user ? (user.role === "admin" ? "/admin" : "/portal") : "/login")}
                  className="rounded-full w-full h-11 bg-black text-white text-[13px]"
                >
                  {user ? L.portal : L.access}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const FOOTER_COPY = {
  en: {
    tagline: "AI compute infrastructure operated from Batam, Indonesia.",
    hq: "Registered in England & Wales · Strategy Intelligence Solutions LTD",
    explore: "Explore",
    company: "Company",
    legal: "Legal",
    disclaimer: "Titan Infra is operated by Strategy Intelligence Solutions LTD. This site is informational only and does not constitute a public offer, solicitation, or financial advice. Participation is by invitation only.",
  },
  fr: {
    tagline: "Infrastructure de calcul IA opérée depuis Batam, en Indonésie.",
    hq: "Immatriculée en Angleterre & Pays de Galles · Strategy Intelligence Solutions LTD",
    explore: "Explorer",
    company: "Société",
    legal: "Mentions légales",
    disclaimer: "Titan Infra est opéré par Strategy Intelligence Solutions LTD. Ce site est informatif et ne constitue ni une offre au public, ni une sollicitation, ni un conseil financier. La participation se fait sur invitation uniquement.",
  },
  es: {
    tagline: "Infraestructura de cálculo IA operada desde Batam, Indonesia.",
    hq: "Registrada en Inglaterra y Gales · Strategy Intelligence Solutions LTD",
    explore: "Explorar",
    company: "Empresa",
    legal: "Legal",
    disclaimer: "Titan Infra es operado por Strategy Intelligence Solutions LTD. Este sitio es informativo y no constituye una oferta al público, solicitud ni consejo financiero. La participación es solo por invitación.",
  },
};

export const Footer = () => {
  const { lang } = useI18n();
  const L = FOOTER_COPY[lang] || FOOTER_COPY.en;
  const N = NAV_LABELS[lang] || NAV_LABELS.en;
  return (
    <footer data-testid="site-footer" className="border-t border-black/5 bg-zinc-50">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="mb-6">
            <Logo theme="dark" size={32} />
          </div>
          <p className="text-[15px] leading-relaxed text-zinc-600 max-w-md">{L.tagline}</p>

          <div className="mt-8 space-y-1.5 text-[12px] leading-relaxed text-zinc-500" data-testid="footer-company-block">
            <p className="text-zinc-700">{COMPANY.legalName}</p>
            <p>{COMPANY.address.line1}, {COMPANY.address.city} {COMPANY.address.postcode}, {COMPANY.address.country}</p>
            <p>Registered in {COMPANY.jurisdiction} · Company No. <span className="font-mono">{COMPANY.companyNumber}</span></p>
            <p className="pt-2">
              <a href={`mailto:${COMPANY.emails.general}`} className="font-mono text-[12px] text-zinc-600 hover:text-black">{COMPANY.emails.general}</a>
              <span className="mx-2 text-zinc-300">·</span>
              <a href={`mailto:${COMPANY.emails.investorRelations}`} className="font-mono text-[12px] text-zinc-600 hover:text-black">{COMPANY.emails.investorRelations}</a>
            </p>
          </div>
        </div>
        <div className="md:col-span-3">
          <p className="text-[11px] tracking-[0.22em] uppercase text-zinc-500 mb-4 font-medium">{L.explore}</p>
          <ul className="space-y-2 text-[14px]">
            <li><Link to="/"        data-testid="footer-home" className="text-zinc-600 hover:text-black">{N.home}</Link></li>
            <li><Link to="/contact" data-testid="footer-contact" className="text-zinc-600 hover:text-black">{N.contact}</Link></li>
            <li><Link to="/login"   data-testid="footer-access" className="text-zinc-600 hover:text-black">{N.access}</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="text-[11px] tracking-[0.22em] uppercase text-zinc-500 mb-4 font-medium">{L.company}</p>
          <ul className="space-y-2 text-[14px]">
            <li><Link to="/contact"  data-testid="footer-contact-2" className="text-zinc-600 hover:text-black">{N.contact}</Link></li>
            <li><Link to="/login"    data-testid="footer-login" className="text-zinc-600 hover:text-black">{N.login}</Link></li>
          </ul>
        </div>
        <div className="md:col-span-2">
          <p className="text-[11px] tracking-[0.22em] uppercase text-zinc-500 mb-4 font-medium">{L.legal}</p>
          <ul className="space-y-2 text-[14px]">
            <li><Link to="/risk-warning" data-testid="footer-risk" className="text-zinc-600 hover:text-black">Risk warning</Link></li>
            <li><Link to="/legal"        data-testid="footer-legal" className="text-zinc-600 hover:text-black">Legal notice</Link></li>
            <li><Link to="/privacy"      data-testid="footer-privacy" className="text-zinc-600 hover:text-black">Privacy</Link></li>
            <li><Link to="/terms"        data-testid="footer-terms" className="text-zinc-600 hover:text-black">Terms</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-black/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-[11px] leading-relaxed text-zinc-500 max-w-3xl">{L.disclaimer}</p>
          <p className="text-[11px] tracking-widest uppercase text-zinc-500 shrink-0">© {new Date().getFullYear()} Titan Infra</p>
        </div>
      </div>
    </footer>
  );
};
